import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import { getSuggestedResponse } from "../../services/api";

const threadSignature = (threadId, posts, reservationStatus) => {
  if (!threadId || !posts) {
    return null;
  }

  const newestPost = posts[0];

  return [
    threadId,
    posts.length,
    newestPost?.id ?? newestPost?.created_at ?? "",
    reservationStatus ?? ""
  ].join("|");
};

/**
 * Fetches the AI draft for the thread on screen; Airlink caches drafts per guest message.
 */
export const useSuggestedResponse = (
  thread,
  booking,
  property,
  guestName,
  additionalUserContext,
  channel
) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The ref lets callbacks captured earlier read the draft a later request produced.
  const [draft, setDraftState] = useState(null);
  const draftRef = useRef(null);

  const threadId = thread?.id;
  const posts = thread?.posts;
  const signature = threadSignature(
    threadId,
    posts,
    thread?.reservation?.status
  );

  const requests = useRef(new Map());

  // A layout effect, so this is current before the fetching effect below runs.
  const currentThreadId = useRef(threadId);
  useLayoutEffect(() => {
    currentThreadId.current = threadId;
  }, [threadId]);

  const currentSignature = useRef(signature);
  useLayoutEffect(() => {
    currentSignature.current = signature;
  }, [signature]);

  const contextRef = useRef(additionalUserContext);
  useLayoutEffect(() => {
    contextRef.current = additionalUserContext;
  }, [additionalUserContext]);

  const readCurrentDraft = useCallback(
    () =>
      draftRef.current?.threadId === currentThreadId.current
        ? draftRef.current
        : null,
    []
  );

  const requestSuggestedResponse = useCallback(
    async ({ explicit = false } = {}) => {
      if (!threadId) {
        return false;
      }

      if (!posts?.[0]?.is_guest) {
        requests.current.delete(threadId);
        setError(null);
        setLoading(false);
        return false;
      }

      // Airlink treats a non-empty context as a cache bypass, so only a regenerate
      // carrying one can produce a different answer to the request in flight.
      const bypassInFlight = explicit && Boolean(contextRef.current?.trim());
      const inFlight = requests.current.get(threadId);
      if (!bypassInFlight && inFlight?.signature === signature) {
        setLoading(true);
        return inFlight.promise;
      }

      const request = { signature };
      const isSuperseded = () => requests.current.get(threadId) !== request;
      const ownsMessengerState = () =>
        !isSuperseded() &&
        currentThreadId.current === threadId &&
        currentSignature.current === signature;

      requests.current.set(threadId, request);
      setLoading(true);
      setError(null);

      request.promise = (async () => {
        try {
          const response = await getSuggestedResponse({
            bookingId: booking?.id,
            messages: posts,
            propertyId: property?.id,
            guestName,
            additionalUserContext: explicit ? contextRef.current : null,
            channel,
            reservation: thread?.reservation
          });

          if (!ownsMessengerState()) {
            return false;
          }

          draftRef.current = {
            threadId,
            signature,
            suggestedResponse: response.data.ai_generated_response,
            recordId: response.data.ai_performance_record_id
          };
          setDraftState(draftRef.current);

          return true;
        } catch (e) {
          if (ownsMessengerState()) {
            setError(true);
          }

          return false;
        } finally {
          const owned = ownsMessengerState();
          if (!isSuperseded()) {
            requests.current.delete(threadId);
          }
          if (owned) {
            setLoading(false);
          }
        }
      })();

      return request.promise;
    },
    [
      threadId,
      posts,
      signature,
      booking?.id,
      property?.id,
      guestName,
      channel,
      thread?.reservation
    ]
  );

  useEffect(() => {
    if (!property?.id || !posts || !threadId) {
      return;
    }

    if (draft?.signature === signature && !requests.current.has(threadId)) {
      setLoading(false);
      return;
    }

    // Only this thread's draft is stale; another thread's is kept for the return to it.
    if (draftRef.current?.threadId === threadId) {
      draftRef.current = null;
      setDraftState(null);
    }

    requestSuggestedResponse();
  }, [
    property?.id,
    posts,
    threadId,
    signature,
    draft,
    requestSuggestedResponse
  ]);

  const fetchSuggestedResponse = useCallback(
    () => requestSuggestedResponse({ explicit: true }),
    [requestSuggestedResponse]
  );

  const getSuggestedResponseValue = useCallback(
    () => readCurrentDraft()?.suggestedResponse,
    [readCurrentDraft]
  );

  const getSuggestedResponseRecordId = useCallback(
    () => readCurrentDraft()?.recordId,
    [readCurrentDraft]
  );

  return {
    loading,
    error,
    getSuggestedResponse: getSuggestedResponseValue,
    getSuggestedResponseRecordId,
    fetchSuggestedResponse
  };
};
